import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CsrWebApiService } from '../../../../csr-web-api.service';
import {
  CAREER_CHALLENGE_KEPT_TYPES,
  CAREER_CHALLENGE_RUBRIC_BANDS,
  CAREER_CHALLENGE_RUBRIC_CRITERIA,
  CAREER_CHALLENGE_RUBRIC_LABELS,
  CAREER_CHALLENGE_TYPE_HELP,
  CAREER_CHALLENGE_IP_POLICIES,
  CAREER_CHALLENGE_IP_POLICY_HELP,
  CAREER_CHALLENGE_IP_POLICY_LABELS,
  CareerChallengeFaqItemDto,
  CareerChallengeIpPolicy,
  CareerChallengeParticipationPolicyDto,
  CareerChallengeRubricWeightDto,
  CareerChallengeSkillMappingDto,
  CareerRolePickerItemResponse,
  CreateCareerChallengeRequest,
  UpdateCareerChallengeRequest,
  defaultCareerChallengeRubricWeight
} from '../../../../models';
import { CareerChallengeAdminStore } from '../shared/career-challenge-admin.store';
import {
  applyDatePart,
  applyTimePart,
  formatTimeInput
} from '../shared/career-challenge-dates.util';
import { ChallengeCoverFieldComponent } from '../../../../shared/challenge-media/challenge-cover-field.component';
import { renderLessonMarkdown } from '../../../../shared/lesson-markdown.util';
import EasyMDE from 'easymde';
import { RefreshChallengeContentComponent } from '../shared/refresh-challenge-content.component';

type KeyDateField = 'registrationOpensAt' | 'registrationClosesAt' | 'startsAt' | 'endsAt' | 'judgingEndsAt';

@Component({
  selector: 'app-career-challenge-overview-tab',
  standalone: true,
  imports: [
    RefreshChallengeContentComponent,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatRadioModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressBarModule,
    MatSnackBarModule,
    ChallengeCoverFieldComponent
  ],
  templateUrl: './overview-tab.component.html',
  styleUrl: './overview-tab.component.scss'
})
export class CareerChallengeOverviewTabComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly api = inject(CsrWebApiService);
  private readonly store = inject(CareerChallengeAdminStore);
  private readonly fb = inject(FormBuilder);
  private readonly snackBar = inject(MatSnackBar);

  readonly types = CAREER_CHALLENGE_KEPT_TYPES;
  readonly typeHelp = CAREER_CHALLENGE_TYPE_HELP;
  readonly ipPolicies = CAREER_CHALLENGE_IP_POLICIES;
  readonly ipPolicyLabels = CAREER_CHALLENGE_IP_POLICY_LABELS;
  readonly ipPolicyHelp = CAREER_CHALLENGE_IP_POLICY_HELP;
  readonly rubricCriteria = [...CAREER_CHALLENGE_RUBRIC_CRITERIA];
  readonly rubricLabels = CAREER_CHALLENGE_RUBRIC_LABELS;
  readonly rubricBands = CAREER_CHALLENGE_RUBRIC_BANDS;

  saving = false;
  suggesting = false;
  composing = false;
  catalogLoading = false;
  publishChecklist: string[] = [];
  roles: CareerRolePickerItemResponse[] = [];
  specializations: { slug: string; name: string }[] = [];
  profiles: string[] = [];
  roleInactiveHint = '';
  catalog: CareerChallengeSkillMappingDto[] = [];
  selectedSkillKeys: string[] = [];
  selectedPrerequisiteSkillIds: string[] = [];
  faqItems: CareerChallengeFaqItemDto[] = [];
  coverDisplayUrl: string | null = null;
  rubricWeights: Record<string, number> = {};
  coveragePreview: { catalogFacetCount: number; coveragePercent: number } | null = null;
  skillsStale = false;
  private lastSuggestSignature = '';
  private baselineKeyDates: Record<KeyDateField, Date | null> | null = null;
  private refreshBaseline = '';

  get hasUnsavedRefreshEdits(): boolean {
    return !this.refreshBaseline || JSON.stringify(this.buildUpdatePayload()) !== this.refreshBaseline;
  }

  readonly keyDates = this.fb.group({
    registrationOpensAt: this.fb.control<Date | null>(null),
    registrationClosesAt: this.fb.control<Date | null>(null),
    startsAt: this.fb.control<Date | null>(null),
    endsAt: this.fb.control<Date | null>(null),
    judgingEndsAt: this.fb.control<Date | null>(null)
  });

  @ViewChild('editor') set editorRef(ref: ElementRef<HTMLTextAreaElement> | undefined) {
    if (ref && !this.easyMDE) {
      this.editorElement = ref;
      this.initEasyMDE(ref.nativeElement);
    }
  }
  editorElement?: ElementRef<HTMLTextAreaElement>;
  private easyMDE?: EasyMDE;

  private initEasyMDE(element: HTMLTextAreaElement): void {
    this.easyMDE = new EasyMDE({
      element: element,
      initialValue: this.form.controls.description.value || '',
      placeholder: 'Write problem definition, task brief, deliverables, success criteria...',
      minHeight: '280px',
      maxHeight: '340px',
      spellChecker: false,
      status: false,
      toolbar: [
        'bold', 'italic', 'heading', '|',
        'quote', 'unordered-list', 'ordered-list', '|',
        'link', 'image', 'code', '|',
        'preview', 'side-by-side', 'fullscreen'
      ]
    });

    this.easyMDE.codemirror.on('change', () => {
      const val = this.easyMDE?.value() ?? '';
      this.form.controls.description.setValue(val);
    });
  }

  private syncDescriptionToEditor(desc: string): void {
    if (this.easyMDE && this.easyMDE.value() !== desc) {
      this.easyMDE.value(desc);
    }
  }

  ngOnDestroy(): void {
    this.easyMDE?.toTextArea();
  }

  readonly postRenderMarkdown = (html: string): string => {
    const raw = this.form?.controls?.description?.value ?? '';
    return renderLessonMarkdown(raw);
  };

  readonly form = this.fb.nonNullable.group({
    title: ['', Validators.required],
    slug: ['', Validators.required],
    type: ['hackathon', Validators.required],
    composePrompt: [''],
    summary: ['', Validators.required],
    description: [''],
    coverImageUrl: [''],
    showOnWebsite: [false],
    ipPolicy: ['participants_retain' as CareerChallengeIpPolicy, Validators.required],
    careerRoleSlug: ['', Validators.required],
    specializationSlug: [{ value: '', disabled: true }],
    seniorityLevel: [{ value: '', disabled: true }, Validators.required],
    accessMode: ['open'],
    additionalRoleSlugs: [''],
    /** solo_only | dual | pair_only */
    participationMode: ['dual'],
    requireCommitContribution: [true],
    minCommitsPerMember: [1, [Validators.required, Validators.min(1)]],
    allowLateRegistrationDuringActive: [false]
  });

  get canGenerateCover(): boolean {
    const title = this.form.controls.title.value?.trim();
    const summary = this.form.controls.summary.value?.trim();
    return !!title && !!summary;
  }

  async onCoverGenerated(response: { key: string; url: string }): Promise<void> {
    this.coverDisplayUrl = response.url;
    const challengeId = this.currentChallengeId();
    if (challengeId) {
      await this.store.load(challengeId, true);
      const detail = this.store.challenge();
      if (detail?.coverImageUrl) {
        this.form.patchValue({ coverImageUrl: detail.coverImageUrl });
        this.coverDisplayUrl = detail.coverDisplayUrl ?? response.url;
      }
    }
  }

  get challenge() {
    return this.store.challenge();
  }

  get loading() {
    return this.isCreateMode ? false : this.store.loading();
  }

  get id(): string {
    return this.route.parent?.snapshot.paramMap.get('id') ?? '';
  }

  get isCreateMode(): boolean {
    const parentPath = this.route.parent?.snapshot.routeConfig?.path ?? '';
    return parentPath === 'new' || this.id === 'new';
  }

  readonly participationModeOptions: { value: string; label: string; hint: string }[] = [
    { value: 'solo_only', label: 'Solo only', hint: '1 person, 1 repo' },
    { value: 'dual', label: 'Dual track (solo + 2-person team)', hint: 'Learner picks solo or claims a seat in a fixed 2-person pair; seats have individual briefs and skill mappings' },
    { value: 'pair_only', label: 'Pair only', hint: 'Must join as a fixed 2-person team; no solo option' }
  ];

  get challengeLabel(): string {
    return this.isCreateMode ? 'Create challenge' : this.challenge?.title || 'Challenge';
  }

  get selectedTypeHelp(): string {
    return this.typeHelp[this.form.controls.type.value] ?? '';
  }

  get selectedIpPolicyHelp(): string {
    const key = this.form.controls.ipPolicy.value as CareerChallengeIpPolicy;
    return this.ipPolicyHelp[key] ?? '';
  }

  get schedulePhaseHelp(): string {
    const status = (this.challenge?.status ?? '').toString().toLowerCase();
    switch (status) {
      case 'active':
        return 'Start date is locked while the challenge is active. Changing Ends will notify registered participants.';
      case 'judging':
        return 'Registration and start dates are locked. Extending Ends will not reopen submissions; participants and judges are notified.';
      case 'registration':
      case 'published':
        return 'Changing key dates after publish notifies registered participants and reschedules activation/judging jobs.';
      case 'completed':
      case 'archived':
        return 'Completed or archived challenges cannot change dates.';
      default:
        return '';
    }
  }

  isKeyDateLocked(field: KeyDateField): boolean {
    const status = (this.challenge?.status ?? '').toString().toLowerCase();
    if (status === 'completed' || status === 'archived') {
      return true;
    }
    if (status === 'active') {
      return field === 'startsAt';
    }
    if (status === 'judging') {
      return field === 'startsAt' || field === 'registrationOpensAt' || field === 'registrationClosesAt';
    }
    return false;
  }

  get canCompose(): boolean {
    return (
      !this.composing &&
      !this.catalogLoading &&
      !!this.form.controls.careerRoleSlug.value.trim() &&
      !!this.form.controls.seniorityLevel.value.trim() &&
      !!this.form.controls.composePrompt.value.trim()
    );
  }

  get canSuggestSkills(): boolean {
    return (
      !this.suggesting &&
      !this.catalogLoading &&
      !!this.form.controls.careerRoleSlug.value.trim() &&
      !!this.form.controls.seniorityLevel.value.trim() &&
      !!this.form.controls.description.value.trim()
    );
  }

  prerequisiteSkillOptions: { skillId: string; skillName: string }[] = [];

  trackBySkillId = (item: { skillId: string; skillName: string }): string => {
    return item.skillId;
  };

  trackByFacet = (facet: CareerChallengeSkillMappingDto): string => {
    return this.facetKey(facet);
  };

  private updatePrerequisiteSkillOptions(): void {
    const byId = new Map<string, string>();
    for (const mapping of this.catalog) {
      if (!mapping.skillId || byId.has(mapping.skillId)) {
        continue;
      }
      byId.set(mapping.skillId, mapping.skillName?.trim() || mapping.skillId);
    }
    this.prerequisiteSkillOptions = [...byId.entries()]
      .map(([skillId, skillName]) => ({ skillId, skillName }))
      .sort((a, b) => a.skillName.localeCompare(b.skillName));
  }

  async ngOnInit(): Promise<void> {
    await this.loadRoles();
    this.seedRubrics(this.form.controls.type.value);
    if (this.isCreateMode) {
      this.store.clear();
      this.applyTypeDefaults(this.form.controls.type.value);
      this.refreshPublishChecklist();
      return;
    }
    await this.reload();
  }

  async reload(): Promise<void> {
    const challengeId = this.currentChallengeId();
    if (!challengeId) {
      return;
    }
    const detail = await this.store.load(challengeId, true);
    if (!detail) {
      return;
    }

    const schedule = detail.schedule;
    const policy = detail.participationPolicy;
    const access = detail.access;

    this.form.patchValue({
      title: detail.title,
      slug: detail.slug,
      type: String(detail.type),
      summary: detail.summary ?? '',
      description: detail.description ?? '',
      coverImageUrl: detail.coverImageUrl ?? '',
      showOnWebsite: detail.showOnWebsite,
      ipPolicy: this.normalizeIpPolicy(detail.ipPolicy),
      careerRoleSlug: detail.audience?.careerRoleSlug ?? '',
      specializationSlug: detail.audience?.specializationSlug ?? '',
      seniorityLevel: (detail.audience?.seniorityLevels ?? [])[0] ?? '',
      accessMode: String(access?.mode ?? 'open'),
      additionalRoleSlugs: (detail.audience?.additionalRoleSlugs ?? []).join(', '),
      participationMode: this.policyToParticipationMode(policy),
      requireCommitContribution: policy?.requireCommitContribution ?? false,
      minCommitsPerMember: policy?.minCommitsPerMember ?? 1,
      allowLateRegistrationDuringActive: policy?.allowLateRegistrationDuringActive ?? false
    });
    this.syncDescriptionToEditor(detail.description ?? '');

    this.keyDates.setValue({
      registrationOpensAt: detail.registrationOpensAt ?? schedule?.registrationOpensAt ?? null,
      registrationClosesAt: detail.registrationClosesAt ?? schedule?.registrationClosesAt ?? null,
      startsAt: detail.startsAt ?? schedule?.startsAt ?? null,
      endsAt: detail.endsAt ?? schedule?.endsAt ?? null,
      judgingEndsAt: schedule?.judgingEndsAt ?? null
    });
    this.baselineKeyDates = { ...this.keyDates.getRawValue() };
    this.applyKeyDateLocks();

    this.coverDisplayUrl = detail.coverDisplayUrl ?? null;
    this.seedRubrics(String(detail.type), detail.rubricWeights ?? []);
    this.selectedSkillKeys = (detail.skillMappings ?? []).map((mapping) => this.facetKey(mapping));
    this.selectedPrerequisiteSkillIds = [...new Set(access?.prerequisiteSkillIds ?? [])];
    this.faqItems = this.normalizeFaqItems(detail.faqItems);
    this.mergeCatalog(detail.skillMappings ?? []);

    await this.loadAudienceOptions();
    await this.loadCatalog();
    this.refreshPublishChecklist();
    this.refreshStaleHint();
    this.refreshBaseline = JSON.stringify(this.buildUpdatePayload());
  }

  onTypeSelected(): void {
    this.applyTypeDefaults(this.form.controls.type.value);
    this.seedRubrics(this.form.controls.type.value, this.selectedRubricWeights());
    this.refreshPublishChecklist();
  }

  onKeyDateModelChange(): void {
    this.refreshPublishChecklist();
  }

  keyDateTime(field: KeyDateField): string {
    return formatTimeInput(this.keyDates.controls[field].value);
  }

  onKeyDatePicked(field: KeyDateField, datePart: Date | null): void {
    const current = this.keyDates.controls[field].value;
    this.keyDates.controls[field].setValue(applyDatePart(current, datePart));
    this.onKeyDateModelChange();
  }

  onKeyTimeChanged(field: KeyDateField, time: string): void {
    const current = this.keyDates.controls[field].value;
    if (!time.trim()) {
      if (current) {
        const cleared = new Date(current.getTime());
        cleared.setHours(0, 0, 0, 0);
        this.keyDates.controls[field].setValue(cleared);
      }
      this.onKeyDateModelChange();
      return;
    }
    this.keyDates.controls[field].setValue(applyTimePart(current, time));
    this.onKeyDateModelChange();
  }

  async onRoleChanged(): Promise<void> {
    this.form.patchValue({ specializationSlug: '', seniorityLevel: '' });
    this.selectedSkillKeys = [];
    this.selectedPrerequisiteSkillIds = [];
    this.coveragePreview = null;
    await this.loadAudienceOptions();
    await this.loadCatalog();
    this.refreshPublishChecklist();
    this.refreshStaleHint();
  }

  async onSpecializationChanged(): Promise<void> {
    this.form.patchValue({ seniorityLevel: '' });
    this.selectedSkillKeys = this.lockedSkillKeys();
    this.selectedPrerequisiteSkillIds = [];
    this.coveragePreview = null;
    await this.loadAudienceOptions();
    await this.loadCatalog();
    this.refreshPublishChecklist();
    this.refreshStaleHint();
  }

  async onProfileChanged(): Promise<void> {
    this.selectedSkillKeys = this.lockedSkillKeys();
    this.selectedPrerequisiteSkillIds = [];
    this.coveragePreview = null;
    await this.loadCatalog();
    this.refreshPublishChecklist();
    this.refreshStaleHint();
  }

  onDescriptionChanged(): void {
    this.refreshPublishChecklist();
    this.refreshStaleHint();
  }

  onAccessModeChanged(): void {
    if (this.form.controls.accessMode.value !== 'prerequisites') {
      this.selectedPrerequisiteSkillIds = [];
    }
    if (this.isFirstSgaEvaluationMode()) {
      this.form.patchValue({ specializationSlug: '' });
    }
    if (this.isCoreSkillsOnlyMode()) {
      this.applyCoreSkillsOnlySelection();
    }
    this.refreshPublishChecklist();
  }

  setRubricWeight(key: string, raw: string | number): void {
    const parsed = Number(raw);
    this.rubricWeights = {
      ...this.rubricWeights,
      [key]: Number.isFinite(parsed) && parsed > 0 ? parsed : 1
    };
  }

  onSkillsChanged(keys: string[]): void {
    if (this.isCoreSkillsOnlyMode()) {
      this.applyCoreSkillsOnlySelection();
      this.refreshPublishChecklist();
      return;
    }
    const locked = this.lockedSkillKeys();
    this.selectedSkillKeys = [...new Set([...locked, ...keys])];
    this.refreshPublishChecklist();
  }

  onPrerequisiteSkillsChanged(skillIds: string[]): void {
    this.selectedPrerequisiteSkillIds = [...new Set(skillIds.filter(Boolean))];
    this.refreshPublishChecklist();
  }

  isLocked(mapping: CareerChallengeSkillMappingDto): boolean {
    return !!mapping.locked || mapping.source === 'fundamental';
  }

  isOpenAccessMode(): boolean {
    return this.form.controls.accessMode.value === 'open';
  }

  isFirstSgaEvaluationMode(): boolean {
    return this.form.controls.accessMode.value === 'first_sga_evaluation';
  }

  isPersonalizedProgressionMode(): boolean {
    return this.form.controls.accessMode.value === 'personalized_progression';
  }

  get publishActionLabel(): string {
    return this.isFirstSgaEvaluationMode() ? 'Activate' : 'Publish';
  }

  get archiveActionLabel(): string {
    return this.isFirstSgaEvaluationMode() ? 'Deactivate' : 'Archive';
  }

  isCoreSkillsOnlyMode(): boolean {
    return this.isOpenAccessMode() || this.isFirstSgaEvaluationMode();
  }

  isCoreSkill(mapping: CareerChallengeSkillMappingDto): boolean {
    return mapping.source === 'fundamental' || mapping.locked === true;
  }

  isSkillOptionDisabled(mapping: CareerChallengeSkillMappingDto): boolean {
    return this.isCoreSkillsOnlyMode() && !this.isCoreSkill(mapping);
  }

  applyCoreSkillsOnlySelection(): void {
    if (!this.isCoreSkillsOnlyMode()) {
      return;
    }
    // Open + first-SGA: locked fundamentals only.
    this.selectedSkillKeys = this.catalog
      .filter((mapping) => this.isCoreSkill(mapping))
      .map((mapping) => this.facetKey(mapping));
  }

  /** @deprecated Use applyCoreSkillsOnlySelection */
  applyOpenAccessSkillSelection(): void {
    this.applyCoreSkillsOnlySelection();
  }

  lockedSkillKeys(): string[] {
    return this.catalog.filter((mapping) => this.isLocked(mapping)).map((mapping) => this.facetKey(mapping));
  }

  facetTargetLabel(mapping: CareerChallengeSkillMappingDto): string {
    const target = mapping.targetProficiency;
    return target == null || target === '' ? '' : `target ${target}`;
  }

  facetKey(mapping: CareerChallengeSkillMappingDto): string {
    return `${mapping.skillId}::${mapping.topicName}`;
  }

  facetLabel(mapping: CareerChallengeSkillMappingDto): string {
    const skill = mapping.skillName?.trim() || mapping.skillId;
    const topic = mapping.topicName?.trim() || skill;
    return skill === topic ? skill : `${skill} / ${topic}`;
  }

  async composeChallenge(): Promise<void> {
    if (!this.canCompose) {
      return;
    }
    const raw = this.form.getRawValue();
    const hasExistingContent =
      !!raw.title.trim() || !!raw.summary.trim() || !!raw.description.trim();
    if (hasExistingContent) {
      const ok = window.confirm(
        'Generate will overwrite Title, Summary, and Description with AI output. Continue?'
      );
      if (!ok) {
        return;
      }
    }
    this.composing = true;
    const previous = [...this.selectedSkillKeys];
    try {
      const response = await firstValueFrom(
        this.api.composeCareerChallenge({
          careerRoleSlug: raw.careerRoleSlug.trim(),
          specializationSlug: this.isFirstSgaEvaluationMode() ? null : raw.specializationSlug.trim() || null,
          seniorityLevels: [raw.seniorityLevel.trim()],
          type: raw.type,
          prompt: raw.composePrompt.trim() || null,
          title: raw.title.trim() || null,
          accessMode: raw.accessMode,
          ipPolicy: raw.ipPolicy
        })
      );
      const skills = response.skills ?? [];
      this.mergeCatalog(skills);
      this.selectedSkillKeys = skills.map((mapping) => this.facetKey(mapping));
      const patch: { title?: string; summary?: string; description?: string } = {};
      if (response.title?.trim()) {
        patch.title = response.title.trim();
      }
      if (response.summary?.trim()) {
        patch.summary = response.summary.trim();
      }
      if (response.description?.trim()) {
        patch.description = response.description.trim();
        this.syncDescriptionToEditor(response.description.trim());
      }
      if (Object.keys(patch).length) {
        this.form.patchValue(patch);
      }
      this.faqItems = this.normalizeFaqItems(response.faqItems);
      this.seedRubrics(raw.type, response.rubricWeights ?? []);
      this.coveragePreview = {
        catalogFacetCount: response.catalogFacetCount,
        coveragePercent: response.coveragePercent
      };
      this.lastSuggestSignature = this.currentSuggestSignature();
      this.skillsStale = false;
      this.refreshPublishChecklist();
      this.snackBar.open(
        `Generated title, description and ${skills.length} skills`,
        'Close',
        { duration: 2400 }
      );
    } catch (error) {
      this.selectedSkillKeys = previous;
      this.snackBar.open(this.readError(error, 'Could not generate the challenge draft.'), 'Close', {
        duration: 4000
      });
    } finally {
      this.composing = false;
    }
  }

  async suggestSkills(): Promise<void> {
    if (!this.canSuggestSkills) {
      return;
    }
    this.suggesting = true;
    const previous = [...this.selectedSkillKeys];
    try {
      const raw = this.form.getRawValue();
      const response = await firstValueFrom(
        this.api.suggestCareerChallengeSkills({
          description: raw.description.trim(),
          careerRoleSlug: raw.careerRoleSlug.trim(),
          specializationSlug: this.isFirstSgaEvaluationMode() ? null : raw.specializationSlug.trim() || null,
          seniorityLevels: raw.seniorityLevel.trim() ? [raw.seniorityLevel.trim()] : null,
          type: raw.type
        })
      );
      const skills = response.skills ?? [];
      this.selectedSkillKeys = [...new Set([...this.lockedSkillKeys(), ...skills.map((mapping) => this.facetKey(mapping))])];
      this.mergeCatalog(skills);
      this.lastSuggestSignature = this.currentSuggestSignature();
      this.skillsStale = false;
      this.refreshPublishChecklist();
      this.snackBar.open(`Suggested ${skills.length} skills`, 'Close', { duration: 2200 });
    } catch (error) {
      this.selectedSkillKeys = previous;
      this.snackBar.open(this.readError(error, 'Could not suggest skills. Previous selection kept.'), 'Close', {
        duration: 4000
      });
    } finally {
      this.suggesting = false;
    }
  }

  async save(): Promise<void> {
    if (this.form.invalid) {
      this.refreshPublishChecklist();
      return;
    }

    this.saving = true;
    try {
      if (this.isCreateMode) {
        const created = await firstValueFrom(this.api.createCareerChallenge(this.buildCreatePayload()));
        this.snackBar.open('Challenge created', 'Close', { duration: 2200 });
        await this.router.navigate(['/admin/career-challenges', created.id, 'overview']);
        return;
      }

      const challengeId = this.currentChallengeId();
      if (!challengeId) {
        return;
      }

      if (!this.confirmLiveScheduleChangesIfNeeded()) {
        return;
      }

      await firstValueFrom(this.api.updateCareerChallenge(challengeId, this.buildUpdatePayload()));
      this.snackBar.open('Saved', 'Close', { duration: 2200 });
      await this.reload();
    } catch (error) {
      this.snackBar.open(this.readError(error), 'Close', { duration: 3500 });
    } finally {
      this.saving = false;
    }
  }

  async publish(): Promise<void> {
    const challengeId = this.currentChallengeId();
    if (!challengeId) {
      return;
    }
    this.refreshPublishChecklist();
    await this.runAction(
      () => firstValueFrom(this.api.publishCareerChallenge(challengeId)),
      this.isFirstSgaEvaluationMode() ? 'Activated' : 'Published'
    );
  }

  async copyChallenge(): Promise<void> {
    const challengeId = this.currentChallengeId();
    if (!challengeId) {
      return;
    }
    this.saving = true;
    try {
      const title = this.challenge?.title
        ? `Copy of ${this.challenge.title}`
        : undefined;
      const created = await firstValueFrom(
        this.api.copyCareerChallenge(challengeId, title ? { title } : {})
      );
      this.snackBar.open('Draft copy created. Set the schedule, then publish.', 'Close', {
        duration: 4000
      });
      void this.router.navigate(['/admin/career-challenges', created.id, 'overview']);
    } catch (error) {
      this.snackBar.open(this.readError(error), 'Close', { duration: 3500 });
    } finally {
      this.saving = false;
    }
  }

  async archive(): Promise<void> {
    const challengeId = this.currentChallengeId();
    if (!challengeId) {
      return;
    }
    await this.runAction(
      () => firstValueFrom(this.api.archiveCareerChallenge(challengeId)),
      this.isFirstSgaEvaluationMode() ? 'Deactivated' : 'Archived'
    );
  }

  async deleteDraft(): Promise<void> {
    const challengeId = this.currentChallengeId();
    if (!challengeId || this.challenge?.status !== 'draft') {
      return;
    }
    const title = this.challenge?.title?.trim() || 'this draft';
    if (!confirm(`Delete draft challenge "${title}"? This cannot be undone.`)) {
      return;
    }
    this.saving = true;
    try {
      await firstValueFrom(this.api.deleteCareerChallenge(challengeId));
      this.snackBar.open('Draft challenge deleted.', 'Close', { duration: 3000 });
      void this.router.navigate(['/admin/career-challenges']);
    } catch (error) {
      this.snackBar.open(this.readError(error), 'Close', { duration: 3500 });
    } finally {
      this.saving = false;
    }
  }

  private currentChallengeId(): string | null {
    return this.id && this.id !== 'new' ? this.id : null;
  }

  private buildCreatePayload(): CreateCareerChallengeRequest {
    const raw = this.form.getRawValue();
    const schedule = this.schedulePayload();
    return {
      title: raw.title.trim(),
      slug: raw.slug.trim() || null,
      type: raw.type,
      summary: raw.summary.trim() || null,
      description: raw.description.trim() || null,
      showOnWebsite: raw.showOnWebsite,
      coverImageUrl: raw.coverImageUrl.trim() || null,
      ipPolicy: raw.ipPolicy,
      faqItems: this.faqPayload(),
      audience: {
        careerRoleSlug: raw.careerRoleSlug.trim(),
        specializationSlug: this.isFirstSgaEvaluationMode() ? null : raw.specializationSlug.trim() || null,
        seniorityLevels: raw.seniorityLevel.trim() ? [raw.seniorityLevel.trim()] : [],
        additionalRoleSlugs: this.additionalRoleSlugs(),
        hardGate: raw.accessMode === 'prerequisites' || raw.accessMode === 'first_sga_evaluation'
      },
      access: {
        mode: raw.accessMode,
        prerequisiteSkillIds: raw.accessMode === 'prerequisites' ? [...this.selectedPrerequisiteSkillIds] : []
      },
      schedule,
      participationPolicy: this.readPolicy(),
      skillMappings: this.selectedMappings(),
      rubricCriteria: [...this.rubricCriteria],
      rubricWeights: this.selectedRubricWeights()
    };
  }

  private buildUpdatePayload(): UpdateCareerChallengeRequest {
    const raw = this.form.getRawValue();
    const schedule = this.schedulePayload();
    return {
      title: raw.title.trim(),
      slug: raw.slug.trim(),
      type: raw.type,
      summary: raw.summary.trim() || null,
      description: raw.description.trim() || null,
      showOnWebsite: raw.showOnWebsite,
      coverImageUrl: raw.coverImageUrl.trim() || null,
      ipPolicy: raw.ipPolicy,
      faqItems: this.faqPayload(),
      audience: {
        careerRoleSlug: raw.careerRoleSlug.trim(),
        specializationSlug: this.isFirstSgaEvaluationMode() ? null : raw.specializationSlug.trim() || null,
        seniorityLevels: raw.seniorityLevel.trim() ? [raw.seniorityLevel.trim()] : [],
        additionalRoleSlugs: this.additionalRoleSlugs(),
        hardGate: raw.accessMode === 'prerequisites' || raw.accessMode === 'first_sga_evaluation'
      },
      access: {
        mode: raw.accessMode,
        prerequisiteSkillIds: raw.accessMode === 'prerequisites' ? [...this.selectedPrerequisiteSkillIds] : []
      },
      schedule,
      registrationOpensAt: schedule.registrationOpensAt,
      registrationClosesAt: schedule.registrationClosesAt,
      startsAt: schedule.startsAt,
      endsAt: schedule.endsAt,
      participationPolicy: this.readPolicy(),
      skillMappings: this.selectedMappings(),
      rubricCriteria: [...this.rubricCriteria],
      rubricWeights: this.selectedRubricWeights()
    };
  }

  private schedulePayload() {
    if (this.isFirstSgaEvaluationMode()) {
      return {
        registrationOpensAt: null,
        registrationClosesAt: null,
        startsAt: null,
        endsAt: null,
        judgingEndsAt: null
      };
    }
    const raw = this.keyDates.getRawValue();
    return {
      registrationOpensAt: raw.registrationOpensAt,
      registrationClosesAt: raw.registrationClosesAt,
      startsAt: raw.startsAt,
      endsAt: raw.endsAt,
      judgingEndsAt: raw.judgingEndsAt
    };
  }

  private applyKeyDateLocks(): void {
    (Object.keys(this.keyDates.controls) as KeyDateField[]).forEach((field) => {
      const control = this.keyDates.controls[field];
      if (this.isKeyDateLocked(field)) {
        control.disable({ emitEvent: false });
      } else {
        control.enable({ emitEvent: false });
      }
    });
  }

  private confirmLiveScheduleChangesIfNeeded(): boolean {
    const status = (this.challenge?.status ?? '').toString().toLowerCase();
    if (!status || status === 'draft' || status === 'completed' || status === 'archived') {
      return true;
    }

    const changed = this.changedKeyDateLabels();
    if (!changed.length) {
      return true;
    }

    return window.confirm(
      `You are changing ${changed.join(', ')} on a live challenge (${status}). ` +
        'Registered participants will be notified. Continue?'
    );
  }

  private changedKeyDateLabels(): string[] {
    if (!this.baselineKeyDates) {
      return [];
    }
    const current = this.keyDates.getRawValue();
    const labels: Record<KeyDateField, string> = {
      registrationOpensAt: 'Registration opens',
      registrationClosesAt: 'Registration closes',
      startsAt: 'Starts',
      endsAt: 'Ends',
      judgingEndsAt: 'Judging ends'
    };
    return (Object.keys(labels) as KeyDateField[]).filter((field) => {
      const left = this.baselineKeyDates?.[field]?.getTime() ?? null;
      const right = current[field]?.getTime() ?? null;
      return left !== right;
    }).map((field) => labels[field]);
  }

  private additionalRoleSlugs(): string[] {
    return this.form.controls.additionalRoleSlugs.value
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
  }

  private selectedRubricWeights(): CareerChallengeRubricWeightDto[] {
    return this.rubricCriteria.map((criterion) => ({
      criterion,
      weight: this.rubricWeights[criterion] ?? defaultCareerChallengeRubricWeight(this.form.controls.type.value, criterion)
    }));
  }

  private selectedMappings(): CareerChallengeSkillMappingDto[] {
    const byKey = new Map(this.catalog.map((mapping) => [this.facetKey(mapping), mapping]));
    return this.selectedSkillKeys
      .map((key) => byKey.get(key))
      .filter((mapping): mapping is CareerChallengeSkillMappingDto => !!mapping);
  }

  addFaqItem(): void {
    this.faqItems = [...this.faqItems, { question: '', answer: '' }];
  }

  removeFaqItem(index: number): void {
    this.faqItems = this.faqItems.filter((_, i) => i !== index);
  }

  private faqPayload(): CareerChallengeFaqItemDto[] {
    return this.normalizeFaqItems(this.faqItems);
  }

  private normalizeFaqItems(items: CareerChallengeFaqItemDto[] | null | undefined): CareerChallengeFaqItemDto[] {
    return (items ?? [])
      .map((item, index) => ({
        id: item.id ?? undefined,
        question: (item.question ?? '').trim(),
        answer: (item.answer ?? '').trim(),
        sortOrder: item.sortOrder ?? index
      }))
      .filter((item) => item.question.length > 0 && item.answer.length > 0);
  }

  private normalizeIpPolicy(value: string | null | undefined): CareerChallengeIpPolicy {
    if (value === 'sponsor_license' || value === 'organizer_assignment') {
      return value;
    }
    return 'participants_retain';
  }

  private seedRubrics(type: string, weights: CareerChallengeRubricWeightDto[] = []): void {
    const byKey = new Map(weights.map((row) => [String(row.criterion), Number(row.weight) || 1]));
    const next: Record<string, number> = {};
    for (const criterion of this.rubricCriteria) {
      next[criterion] = byKey.get(criterion) ?? defaultCareerChallengeRubricWeight(type, criterion);
    }
    this.rubricWeights = next;
  }

  private mergeCatalog(skills: CareerChallengeSkillMappingDto[]): void {
    const byKey = new Map(this.catalog.map((mapping) => [this.facetKey(mapping), mapping]));
    for (const skill of skills) {
      byKey.set(this.facetKey(skill), skill);
    }
    this.catalog = [...byKey.values()];
    this.updatePrerequisiteSkillOptions();
  }

  private async loadRoles(): Promise<void> {
    try {
      const page = await firstValueFrom(this.api.listCareerRolesPicker(1, 100, undefined, true));
      this.roles = page.data ?? [];
      const current = this.form.controls.careerRoleSlug.value;
      this.roleInactiveHint = current && !this.roles.some((role) => role.roleSlug === current)
        ? 'This role is no longer active.'
        : '';
    } catch {
      this.roles = [];
    }
  }

  private async loadAudienceOptions(): Promise<void> {
    const roleSlug = this.form.controls.careerRoleSlug.value.trim();
    if (!roleSlug) {
      this.specializations = [];
      this.profiles = [];
      this.roleInactiveHint = '';
      this.form.controls.seniorityLevel.disable();
      this.form.controls.specializationSlug.disable();
      return;
    }

    this.form.controls.seniorityLevel.enable();
    this.form.controls.specializationSlug.enable();

    try {
      const specializationSlug = this.form.controls.specializationSlug.value.trim() || null;
      const options = await firstValueFrom(this.api.getCareerChallengeAudienceOptions(roleSlug, specializationSlug));
      this.specializations = options.specializations ?? [];
      this.profiles = options.seniorityLevels ?? [];
      this.roleInactiveHint = options.roleActive === false ? 'This role is no longer active.' : '';

      const currentSpecialization = this.form.controls.specializationSlug.value;
      if (currentSpecialization && !this.specializations.some((spec) => spec.slug === currentSpecialization)) {
        this.form.patchValue({ specializationSlug: '' });
      }

      const currentProfile = this.form.controls.seniorityLevel.value;
      if (currentProfile && !this.profiles.includes(currentProfile)) {
        this.form.patchValue({ seniorityLevel: this.profiles[0] ?? '' });
      } else if (!currentProfile && this.profiles.length) {
        this.form.patchValue({ seniorityLevel: this.profiles[0] });
      }
    } catch {
      this.specializations = [];
      this.profiles = [];
      this.roleInactiveHint = 'This role is no longer active.';
    }
  }

  private async loadCatalog(): Promise<void> {
    const role = this.form.controls.careerRoleSlug.value.trim();
    const profile = this.form.controls.seniorityLevel.value.trim();
    if (!role || !profile) {
      this.catalog = this.catalog.filter((mapping) => this.isLocked(mapping));
      this.updatePrerequisiteSkillOptions();
      this.selectedPrerequisiteSkillIds = [];
      return;
    }

    this.catalogLoading = true;
    try {
      const specialization = this.form.controls.specializationSlug.value.trim() || null;
      const response = await firstValueFrom(this.api.getCareerChallengeSkillCatalog(role, specialization, [profile]));
      const locked = this.catalog.filter((mapping) => this.isLocked(mapping));
      this.catalog = response.skills ?? [];
      this.mergeCatalog(locked);
      this.updatePrerequisiteSkillOptions();

      const allowedFacetKeys = new Set(this.catalog.map((mapping) => this.facetKey(mapping)));
      const allowedSkillIds = new Set(this.catalog.map((mapping) => mapping.skillId));
      const keepLocked = this.lockedSkillKeys();
      this.selectedSkillKeys = this.selectedSkillKeys.filter((key) => allowedFacetKeys.has(key) || keepLocked.includes(key));
      this.selectedPrerequisiteSkillIds = this.selectedPrerequisiteSkillIds.filter((skillId) => allowedSkillIds.has(skillId));
      this.applyCoreSkillsOnlySelection();
    } catch {
      this.catalog = this.catalog.filter((mapping) => this.isLocked(mapping));
      this.updatePrerequisiteSkillOptions();
      this.selectedPrerequisiteSkillIds = [];
    } finally {
      this.catalogLoading = false;
    }
  }

  private applyTypeDefaults(type: string): void {
    if (type === 'coding') {
      this.form.patchValue({
        participationMode: 'solo_only',
        requireCommitContribution: false,
        minCommitsPerMember: 1
      });
      return;
    }

    if (type === 'ai') {
      this.form.patchValue({
        participationMode: 'solo_only',
        requireCommitContribution: true,
        minCommitsPerMember: 1
      });
      return;
    }

    // hackathon default: dual track (solo + 2-person pair)
    this.form.patchValue({
      participationMode: 'dual',
      requireCommitContribution: true,
      minCommitsPerMember: 1
    });
  }

  /** Derive a participationMode string from legacy participation policy fields. */
  private policyToParticipationMode(policy?: CareerChallengeParticipationPolicyDto | null): string {
    if (!policy) return 'dual';
    const { allowSolo, maxTeamSize } = policy;
    if (allowSolo && maxTeamSize <= 1) return 'solo_only';
    if (!allowSolo && maxTeamSize >= 2) return 'pair_only';
    return 'dual';
  }

  refreshPublishChecklist(): void {
    const raw = this.form.getRawValue();
    const items: string[] = [];
    if (!raw.title.trim()) items.push('Name');
    if (!raw.slug.trim()) items.push('Slug');
    if (!raw.careerRoleSlug.trim()) items.push('Role');
    if (!raw.seniorityLevel.trim()) items.push('Profile');
    if (!raw.summary.trim()) items.push('Summary');
    if (!raw.description.trim()) items.push('Description');
    if (!this.isFirstSgaEvaluationMode() && (!this.keyDates.controls.startsAt.value || !this.keyDates.controls.endsAt.value)) {
      items.push('Schedule starts/ends');
    }
    if (this.selectedSkillKeys.length === 0) items.push('Challenge skills');
    if (raw.accessMode === 'prerequisites' && this.selectedPrerequisiteSkillIds.length === 0) {
      items.push('Prerequisite skills');
    }
    this.publishChecklist = items;
  }

  private currentSuggestSignature(): string {
    const raw = this.form.getRawValue();
    return [
      raw.careerRoleSlug.trim(),
      raw.specializationSlug.trim(),
      raw.seniorityLevel.trim(),
      raw.summary.trim(),
      raw.description.trim()
    ].join('|');
  }

  private refreshStaleHint(): void {
    this.skillsStale = !!this.lastSuggestSignature && this.lastSuggestSignature !== this.currentSuggestSignature();
  }

  private readPolicy(): CareerChallengeParticipationPolicyDto {
    const raw = this.form.getRawValue();
    const mode = raw.participationMode;
    const allowSolo = mode !== 'pair_only';
    const minTeamSize = mode === 'solo_only' ? 1 : 2;
    const maxTeamSize = mode === 'solo_only' ? 1 : 2;
    return {
      allowSolo,
      minTeamSize,
      maxTeamSize,
      requireCommitContribution: raw.requireCommitContribution,
      minCommitsPerMember: Number(raw.minCommitsPerMember),
      allowLateRegistrationDuringActive: raw.allowLateRegistrationDuringActive
    };
  }

  private async runAction(action: () => Promise<unknown>, ok: string): Promise<void> {
    this.saving = true;
    try {
      await action();
      this.snackBar.open(ok, 'Close', { duration: 2200 });
      await this.reload();
    } catch (error) {
      this.snackBar.open(this.readError(error), 'Close', { duration: 3500 });
    } finally {
      this.saving = false;
    }
  }

  private readError(error: unknown, fallback = 'Request failed'): string {
    if (error instanceof HttpErrorResponse) {
      const detail = error.error?.detail ?? error.error?.title ?? error.error;
      if (typeof detail === 'string' && detail.trim()) {
        return detail;
      }
    }
    return fallback;
  }
}
